import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Upload, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";

const CATEGORIES = ["Driveways", "Patios", "Sidewalks", "Garage & Basement Floors", "Stamped & Colored", "Flatwork", "Commercial"];

function UploadBox({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("scope", "gallery");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(fileUrl(data.id));
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="font-semibold">{label}</Label>
      <div onClick={() => ref.current?.click()} className="mt-1.5 border-2 border-dashed border-border hover:border-primary cursor-pointer aspect-video flex items-center justify-center overflow-hidden bg-muted/40" data-testid={testid}>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
        {busy ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : value ? <img src={value} alt="preview" className="w-full h-full object-cover" /> : <div className="text-center text-muted-foreground text-sm"><Upload className="h-5 w-5 mx-auto mb-1" />Upload</div>}
      </div>
    </div>
  );
}

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { title: "", category: "Driveways", location: "", description: "", type: "single", image: "", before_image: "", after_image: "", featured: false };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || (form.type === "single" && !form.image) || (form.type === "before_after" && (!form.before_image || !form.after_image))) {
      toast.error("Add a title and the required image(s).");
      return;
    }
    setSaving(true);
    try {
      await api.post("/gallery", form);
      toast.success("Project added");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/gallery/${id}`); setItems((i) => i.filter((x) => x.id !== id)); toast.success("Removed"); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Gallery</h1>
          <p className="text-muted-foreground">Manage project photos & before/after sets.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary rounded-sm font-bold" data-testid="add-gallery-btn"><Plus className="h-4 w-4 mr-1" /> Add Project</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Add Gallery Project</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="font-semibold">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 rounded-sm" data-testid="gallery-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="font-semibold">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="gallery-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="font-semibold">Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Watertown, WI" className="mt-1.5 rounded-sm" data-testid="gallery-location" /></div>
              </div>
              <div><Label className="font-semibold">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="mt-1.5 rounded-sm" data-testid="gallery-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="single">Single Photo</SelectItem><SelectItem value="before_after">Before / After</SelectItem></SelectContent></Select>
              </div>
              {form.type === "single" ? (
                <UploadBox label="Photo" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="gallery-upload-single" />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <UploadBox label="Before" value={form.before_image} onChange={(v) => setForm({ ...form, before_image: v })} testid="gallery-upload-before" />
                  <UploadBox label="After" value={form.after_image} onChange={(v) => setForm({ ...form, after_image: v })} testid="gallery-upload-after" />
                </div>
              )}
              <div><Label className="font-semibold">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1.5 rounded-sm" data-testid="gallery-description" /></div>
              <Button onClick={save} disabled={saving} className="w-full bg-primary rounded-sm font-bold" data-testid="gallery-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Project"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className="bg-card border border-border overflow-hidden group relative" data-testid={`gallery-admin-${g.id}`}>
            <img src={g.type === "before_after" ? g.after_image : g.image} alt={g.title} className="w-full aspect-square object-cover" />
            <button onClick={() => remove(g.id)} className="absolute top-2 right-2 bg-white/90 hover:bg-destructive hover:text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`gallery-delete-${g.id}`}><Trash2 className="h-4 w-4" /></button>
            <div className="p-3">
              <div className="font-bold text-sm truncate">{g.title}</div>
              <div className="text-xs text-muted-foreground">{g.category}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
